import { keepPreviousData, QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { Optional, WithStringValues } from "common/utils/types";
import { useLatestRef } from "common/hooks";
import { useFetchQuery, useStateQuery } from "common/queries/hooks";

import { useBreakpoints } from "client/common/hooks";

import { clearedFilterValues, hasAnySelectedFilterValue, filtersWithOptions } from "../utils";
import {
  BrowseSearch,
  BrowseAllPatientKind,
  IProductLineDetails,
  IProductQueryResponse,
  ProductLineSearch,
  ProductLineListSearch,
  ProductDealsSearch,
  ProductDeals,
  ProductSearchSearch,
  ProductSearch,
  IClientBrowse,
  CarouselDisplay,
  IncentiveDetails,
  IncentiveItemsSearch,
  RebateDetails,
  RebateItemsSearch,
  FilterOptionsState,
  ProductLineSearchFilters,
  ProductSearchFilters,
  ClientCategory,
  PromotionItems,
  ProductLineAvailabilitySearch,
  ProductLineAvailabilityResponse,
  ItemPricingSearch,
  ItemPricingResponse,
  FilterDefinition,
  FilterValue,
} from "../types";
import {
  keys,
  urls,
  FilterOptionScope,
  PRODUCT_DEALS_FILTER_SCOPE,
  PRODUCT_SEARCH_FILTER_SCOPE,
} from "./constants";

export const useProductLineQuery = (id: number) => {
  return useFetchQuery<IProductLineDetails>(keys.productLineDetail(id), {
    fetch: { url: urls.productLine(id) },
    config: { enabled: !!id },
  });
};

export const useProductQuery = (id: number, { enabled = true }: { enabled?: boolean } = {}) => {
  return useFetchQuery<IProductQueryResponse>(keys.productDetail(id), {
    fetch: { url: urls.product(id) },
    config: { enabled: enabled && !!id },
  });
};

/**
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 * */
export const useCategoryProductLineFilterOptionState = (categoryIds: number[]) => {
  return useStateQuery<FilterOptionsState<ProductLineSearchFilters> | null>({
    queryKey: keys.categoryProductLineFilterOptionState(categoryIds),
    default: null,
  });
};

export const useProductLineSearchQuery = (
  search: ProductLineListSearch,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  const latestSearch = useLatestRef(search);
  const query = useFetchQuery<ProductLineSearch, WithStringValues<ProductLineListSearch>>(
    keys.productLineSearch(search),
    {
      fetch: { url: urls.productLineSearch(), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
  const latestData = useLatestRef(query.data);

  // we only want to update the filter options if the search query has changed to have a stable set
  // of filters for a given search query on the category page.
  // TODO sc-32581 Remove once we have a stable BE filter set returned on each search
  // or a separate endpoint for getting the filters: https://app.shortcut.com/vetcove/story/32581/
  useEffect(() => {
    // Ignore placeholder/previous-key data so we don't cache filters under the new `q`.
    if (query.isPlaceholderData) {
      return;
    }
    const results = latestData.current;
    if (!results) {
      return;
    }
    const currentSearch = latestSearch.current;
    const key = keys.categoryProductLineFilterOptionState(currentSearch.category_ids ?? []);
    const currentFilters = queryClient.getQueryData<
      FilterOptionsState<ProductLineSearchFilters> | undefined
    >(key);

    if (!currentFilters || currentFilters.q !== currentSearch.q) {
      queryClient.setQueryData<FilterOptionsState<ProductLineSearchFilters>>(key, {
        q: currentSearch.q,
        filters: results.filters,
      });
    }
  }, [query.dataUpdatedAt, query.isPlaceholderData, latestData, latestSearch, queryClient]);

  return query;
};

/**
 * Store a response's filter options for its scope, unless the scope already has a set for this
 * `q`.
 *
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 */
const cacheProductSearchFilterOptions = ({
  queryClient,
  scope,
  q,
  filters,
}: {
  queryClient: QueryClient;
  scope: FilterOptionScope;
  q: string;
  filters: ProductSearchFilters;
}) => {
  const key = keys.productFilterOptionState(scope);
  const current = queryClient.getQueryData<FilterOptionsState<ProductSearchFilters> | undefined>(
    key,
  );
  if (!current || current.q !== q) {
    queryClient.setQueryData<FilterOptionsState<ProductSearchFilters>>(key, {
      q,
      filters,
    });
  }
};

export const useProductFilterOptionState = (scope: FilterOptionScope) => {
  return useStateQuery<FilterOptionsState<ProductSearchFilters> | null>({
    queryKey: keys.productFilterOptionState(scope),
    default: null,
  });
};

export const useProductSearchQuery = (
  search: WithStringValues<ProductSearchSearch>,
  {
    enabled = true,
    filterOptionsScope = PRODUCT_SEARCH_FILTER_SCOPE,
  }: {
    enabled?: boolean;
    /** Which surface's options this response describes. Surfaces must not share an entry. */
    filterOptionsScope?: FilterOptionScope;
  } = {},
) => {
  const queryClient = useQueryClient();
  const query = useFetchQuery<ProductSearch, WithStringValues<ProductSearchSearch>>(
    keys.productSearch(search),
    {
      fetch: {
        url: urls.productSearch(),
        body: search,
      },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );

  const cacheSearchRef = useLatestRef((results: ProductSearch) => {
    // Ignore placeholder/previous-key data so we don't cache filters under the new `q`.
    if (query.isPlaceholderData) {
      return;
    }

    cacheProductSearchFilterOptions({
      queryClient,
      scope: filterOptionsScope,
      q: search.q,
      filters: results.filters,
    });
  });

  useEffect(() => {
    // Cache the filters if the query data is available.
    if (query.data) {
      cacheSearchRef.current(query.data);
    }
  }, [query.data, cacheSearchRef]);

  return query;
};

export const useProductDealsSearchQuery = (
  search: WithStringValues<ProductDealsSearch>,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  const query = useFetchQuery<ProductDeals, WithStringValues<ProductDealsSearch>>(
    keys.productDealsSearch(search),
    {
      fetch: { url: urls.productDealsSearch(), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );

  const cacheSearchRef = useLatestRef((results: ProductDeals) => {
    if (query.isPlaceholderData) {
      return;
    }
    cacheProductSearchFilterOptions({
      queryClient,
      scope: PRODUCT_DEALS_FILTER_SCOPE,
      q: "",
      filters: results.filters,
    });
  });

  useEffect(() => {
    // Cache the filters if the query data is available.
    if (query.data) {
      cacheSearchRef.current(query.data);
    }
  }, [query.data, cacheSearchRef]);

  return query;
};

/**
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 * */
export const useRebateProductFilterOptionState = () => {
  return useStateQuery<FilterOptionsState<ProductSearchFilters> | null>({
    queryKey: keys.rebateProductFilterOptionState(),
    default: null,
  });
};

export const useRebateItemsQuery = (id: number, search: RebateItemsSearch) => {
  const queryClient = useQueryClient();
  const query = useFetchQuery<PromotionItems, WithStringValues<RebateItemsSearch>>(
    keys.rebateItemList(id, search),
    {
      fetch: { url: urls.rebateItems(id), body: search },
      config: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const latestData = useLatestRef(query.data);

  // we only want to update the filter options if the search query has changed to have a stable set
  // of filters for a given search query.
  // TODO sc-32581 Remove once we have a stable BE filter set returned on each search
  // or a separate endpoint for getting the filters: https://app.shortcut.com/vetcove/story/32581/
  useEffect(() => {
    // Ignore placeholder/previous-key data so we don't cache stale filters.
    if (query.isPlaceholderData) {
      return;
    }
    const results = latestData.current;
    if (!results) {
      return;
    }
    const currentFilters = queryClient.getQueryData<
      FilterOptionsState<ProductSearchFilters> | undefined
    >(keys.rebateProductFilterOptionState());

    if (!currentFilters) {
      queryClient.setQueryData<FilterOptionsState<ProductSearchFilters>>(
        keys.rebateProductFilterOptionState(),
        { q: "", filters: results.filters },
      );
    }
  }, [query.dataUpdatedAt, query.isPlaceholderData, latestData, queryClient]);

  return query;
};

/**
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 * */
export const useIncentiveProductFilterOptionState = () => {
  return useStateQuery<FilterOptionsState<ProductSearchFilters> | null>({
    queryKey: keys.incentiveProductFilterOptionState(),
    default: null,
  });
};

export const useIncentiveItemsQuery = (
  id: number,
  search: IncentiveItemsSearch,
  { enabled = !!id }: { enabled?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  const query = useFetchQuery<PromotionItems, WithStringValues<IncentiveItemsSearch>>(
    keys.incentiveItemList(id, search),
    {
      fetch: { url: urls.incentiveItems(id), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );

  const cacheSearchRef = useLatestRef((results: PromotionItems) => {
    // Ignore placeholder/previous-key data so we don't cache stale filters.
    if (query.isPlaceholderData) {
      return;
    }

    // we only want to update the filter options if the search query has changed to have a stable set
    // of filters for a given search query.
    // TODO sc-32581 Remove once we have a stable BE filter set returned on each search
    // or a separate endpoint for getting the filters: https://app.shortcut.com/vetcove/story/32581/
    const currentFilters = queryClient.getQueryData<
      FilterOptionsState<ProductSearchFilters> | undefined
    >(keys.incentiveProductFilterOptionState());

    if (!currentFilters) {
      queryClient.setQueryData<FilterOptionsState<ProductSearchFilters>>(
        keys.incentiveProductFilterOptionState(),
        { q: "", filters: results.filters },
      );
    }
  });
  useEffect(() => {
    // Cache the filters if the query data is available.
    if (query.data) {
      cacheSearchRef.current(query.data);
    }
  }, [query.data, cacheSearchRef]);

  return query;
};

export const useBrowseQuery = (
  search: WithStringValues<BrowseSearch> = {
    species: BrowseAllPatientKind.ALL,
  },
  { enabled }: { enabled: boolean },
) => {
  return useFetchQuery<IClientBrowse, WithStringValues<BrowseSearch>>(keys.browseFilter(search), {
    fetch: { url: urls.browse(), body: search },
    config: { placeholderData: keepPreviousData, enabled },
  });
};

export const useCarouselDisplayQuery = (
  search: WithStringValues<BrowseSearch> = {
    species: BrowseAllPatientKind.ALL,
  },
  { enabled }: { enabled: boolean },
) => {
  return useFetchQuery<CarouselDisplay, WithStringValues<BrowseSearch>>(
    keys.carouselsFilter(search),
    {
      fetch: { url: urls.carousels(), body: search },
      config: { placeholderData: keepPreviousData, enabled },
    },
  );
};

export const useCategoryQuery = (id: number) => {
  return useFetchQuery<ClientCategory>(keys.categoryDetail(id), {
    fetch: { url: urls.category(id) },
  });
};

export const useIncentiveQuery = (id: number = 0) => {
  return useFetchQuery<IncentiveDetails>(keys.incentiveDetail(id), {
    fetch: { url: urls.incentive(id) },
    config: { enabled: !!id },
  });
};

export const useRebateQuery = (id: number) => {
  return useFetchQuery<RebateDetails>(keys.rebateDetail(id), {
    fetch: { url: urls.rebate(id) },
  });
};

export const useProductLineAvailabilityQuery = (
  id: number,
  search: WithStringValues<ProductLineAvailabilitySearch>,
) => {
  return useFetchQuery<
    ProductLineAvailabilityResponse,
    WithStringValues<ProductLineAvailabilitySearch>
  >(keys.productLineDetailAvailability(id, search), {
    fetch: { url: urls.productLineAvailability(id), body: search },
  });
};

/**
 * Query to fetch accurate server-side pricing for add-to-cart button
 * Returns total price including discounts, fees, and shipping with MAP pricing enforcement
 */
export const useItemAddToCartPricing = (
  itemId: number,
  quantity: number,
  isSubscriptionPrice?: boolean,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ItemPricingResponse, WithStringValues<ItemPricingSearch>>(
    keys.itemPrices(itemId, {
      quantity,
      is_subscription_price: isSubscriptionPrice ?? false,
    }),
    {
      fetch: {
        url: urls.itemAddToCartPricing(itemId),
        body: {
          quantity: quantity.toString(),
          is_subscription_price: isSubscriptionPrice?.toString() ?? "false",
        },
      },
      config: {
        enabled: enabled && !!itemId && quantity > 0,
      },
    },
  );
};

type FilterPanelOptions<TSearch, TFilterOptions> = {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  /**
   * The search response itself, so its options and its `has_complete_filter_options` cannot come
   * from different ones. Follow that field rather than the feature flag, which a session
   * snapshots at load and keeps until a refresh.
   */
  response: Optional<{ filters: TFilterOptions; has_complete_filter_options: boolean }>;
  /** Options frozen from the session's first response, used when later responses narrow them. */
  cachedFilterOptions: TFilterOptions | undefined;
  filters: TSearch;
  onChange: (next: Partial<TSearch>) => void;
  /** Set false by a surface that hides its filters even when the options would fill a panel. */
  isEnabled?: boolean;
};

/**
 * Picks between this response's filter options and the frozen first-load cache. An incomplete
 * response only lists options that survived the shopper's own filtering, so its lists shrink as
 * they narrow; the cache does not.
 */
export const useFilterPanel = <TSearch, TFilterOptions>({
  definitions,
  response,
  cachedFilterOptions,
  filters,
  onChange,
  isEnabled = true,
}: FilterPanelOptions<TSearch, TFilterOptions>) => {
  const { isTabletLandscapeAndSmaller } = useBreakpoints();
  const hasCompleteOptions = !!response?.has_complete_filter_options;
  const filterOptions = hasCompleteOptions ? response?.filters : cachedFilterOptions;

  const showFilterPanel =
    isEnabled &&
    hasCompleteOptions &&
    !isTabletLandscapeAndSmaller &&
    !!filterOptions &&
    filtersWithOptions({ definitions, filterOptions }).length > 0;

  return {
    filterOptions,
    hasCompleteOptions,
    showFilterPanel,
    hasSelectedFilters: hasAnySelectedFilterValue(definitions, filters),
    clearAll: () => onChange(clearedFilterValues(definitions)),
    // Echoed back so the renderers below need only this one object.
    definitions,
    filters,
    onChange,
  };
};

export type FilterPanelState<TSearch, TFilterOptions> = ReturnType<
  typeof useFilterPanel<TSearch, TFilterOptions>
>;
